import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import api from "../../services/api";
import DataTable from "../../components/common/DataTable/DataTable";
import Button from "../../components/common/Button/Button";
import Input from "../../components/common/Input/Input";
import styles from "./Invoice.module.css";
import {usePermission} from "../../hooks/usePermissions";
import FullscreenModal from "../../components/common/FullscreenModal/FullscreenModal";
import CreateInvoiceForm from "./CreateInvoiceForm";
import { formatLocalDateTime } from "../../utils/datetime";

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const PAGE_SIZE = 10;

const InvoiceList = () => {
  const navigate = useNavigate();
  const {hasPermission} = usePermission();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDraftInvoice, setEditingDraftInvoice] = useState(null);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("internal_transaction_id");
  const [sortOrder, setSortOrder] = useState("DESC");

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await api.get("/invoice");
      if (res.status || res.result) {
        setInvoices(res.result || []);
      } else {
        toast.error("Failed to fetch invoices");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await api.delete(`/invoice/${id}`);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error(error.message || "Failed to delete invoice");
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm("Resend this notification?")) return;
    try {
      await api.post(`/invoice/${id}/resend`);
      toast.success("Notification resent successfully!");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to resend");
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(key);
      setSortOrder("ASC");
    }
    setPage(1);
  };

  const formatDate = (val) => formatLocalDateTime(val, "N/A");
  const fmt = (val) => (val !== null && val !== undefined && val !== "" ? val : "N/A");

  const getStatusClass = (status) => {
    if (!status) return "";
    const s = status.toLowerCase();
    if (s === "success" || s === "completed") return styles.statusSuccess;
    if (s === "failed" || s === "error") return styles.statusFailed;
    return "";
  };

  // Filter
  const filtered = invoices.filter((inv) => {
    const q = appliedSearch.toLowerCase();
    const matchSearch =
      !q ||
      String(inv.internal_transaction_id || "").includes(q) ||
      (inv.plan_name || "").toLowerCase().includes(q) ||
      (inv.status || "").toLowerCase().includes(q) ||
      String(inv.user_id || "").includes(q);

    // Date filter
    let matchDate = true;
    if (fromDate || toDate) {
      const txDate = new Date(inv.created_at);
      txDate.setHours(0, 0, 0, 0);

      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (txDate < from) matchDate = false;
      }
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (txDate > to) matchDate = false;
      }
    }

    return matchSearch && matchDate;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const valA = a[sortBy] ?? "";
    const valB = b[sortBy] ?? "";
    if (valA < valB) return sortOrder === "ASC" ? -1 : 1;
    if (valA > valB) return sortOrder === "ASC" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = [
    { key: "internal_transaction_id", label: "TX ID", sortable: true },
    { key: "user_id", label: "User ID", sortable: true },
    { key: "user_name", label: "User Name", sortable: true },
    { key: "plan_name", label: "Plan Name", sortable: true },
    { key: "plan_validity", label: "Validity", sortable: true },
    {
      key: "invoice_amount_net",
      label: "Net Amount",
      sortable: true,
      render: (val) => `₹${parseFloat(val || 0).toFixed(2)}`,
    },
    {
      key: "total_invoice_amount",
      label: "Total (GST)",
      sortable: true,
      render: (val) => `₹${parseFloat(val || 0).toFixed(2)}`,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (val) => (
        <span className={`${styles.statusBadge} ${getStatusClass(val)}`}>
          {val || "Unknown"}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created At",
      sortable: true,
      render: (val) => formatDate(val),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className={styles.actionsCell}>
          {hasPermission("DELETE_INVOICE") && (
            <button
              className={`${styles.iconBtn} ${styles.delete}`}
              onClick={() => handleDelete(row.internal_transaction_id)}
              title="Delete"
            >
              <TrashIcon />
            </button>
          )}  
          {(hasPermission("EDIT_INVOICE") || (row.status?.trim().toUpperCase() === "DRAFT" && hasPermission("CREATE_INVOICE"))) && (
            <button
              className={`${styles.iconBtn} ${styles.edit}`}
              onClick={(e) => {
                e.stopPropagation();
                if (row.status?.trim().toUpperCase() === "DRAFT") {
                  setEditingDraftInvoice(row);
                } else {
                  navigate(`/invoice/add/${row.internal_transaction_id}`, { state: { invoice: row } });
                }
              }}
              title={row.status?.trim().toUpperCase() === "DRAFT" ? "Edit Draft" : "View / Manage"}
            >
              <EditIcon />
            </button>
          )}
          {row.status?.trim().toUpperCase() === "DRAFT" && (
            <button
              className={styles.iconBtn}
              style={{ color: "#10b981", borderColor: "#10b981" }}
              onClick={(e) => {
                e.stopPropagation();
                handleResend(row.internal_transaction_id);
              }}
              title="Resend Notification"
            >
              <SendIcon />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className={styles.pageHeader}>
        <div className={styles.topRow}>
          <div>
            <h2 className={styles.pageTitle}>Invoices</h2>
            <p className={styles.pageSubtitle}>{filtered.length} invoice{filtered.length !== 1 ? "s" : ""} found</p>
          </div>
          {hasPermission("CREATE_INVOICE") && (
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
              + Add Invoice
            </Button>
          )}
        </div>
        <div className={styles.filterRow}>
          <Input
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
          />
          <Input
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
          />
          <div className={styles.searchGroup}>
            <Input
              className={styles.searchInputWrap}
              label="Search"
              placeholder="Search by ID, User, TX..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={() => { setAppliedSearch(search); setPage(1); }}
              className={styles.searchButton}
            >
              Search
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className={styles.loadingState}>Loading...</p>
      ) : (
        <div className={styles.tableCard}>
          <DataTable
            columns={columns}
            data={paginated}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </div>
      )}

      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} entries
        </div>
        <div className={styles.paginationControls}>
          <Button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            variant="secondary"
            size="small"
          >
            &lt;
          </Button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
            if (
              p === 1 ||
              p === totalPages ||
              (p >= page - 1 && p <= page + 1)
            ) {
              return (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${page === p ? styles.active : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              );
            }
            if (p === page - 2 || p === page + 2) {
              return <span key={p} className={styles.paginationEllipsis}>...</span>;
            }
            return null;
          })}

          <Button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            variant="secondary"
            size="small"
          >
            &gt;
          </Button>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedInvoice && (
        <div className={styles.modalOverlay} onClick={() => setSelectedInvoice(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedInvoice(null)}>&times;</button>

            <div className={styles.modalHeader}>
              <h2>Invoice Details</h2>
              <p>TX ID: <strong>{selectedInvoice.internal_transaction_id}</strong></p>
            </div>

            <div className={styles.grid}>
              <div><span className={styles.fieldLabel}>User ID</span><div className={styles.fieldValue}>{fmt(selectedInvoice.user_id)}</div></div>
              <div><span className={styles.fieldLabel}>User GST Number</span><div className={styles.fieldValue}>{fmt(selectedInvoice.user_gst_number)}</div></div>
              <div className={styles.gridColSpan2}><span className={styles.fieldLabel}>User Address</span><div className={styles.fieldValue}>{fmt(selectedInvoice.user_address)}</div></div>

              <div><span className={styles.fieldLabel}>Plan Name</span><div className={styles.fieldValue}>{fmt(selectedInvoice.plan_name)}</div></div>
              <div><span className={styles.fieldLabel}>Plan Validity</span><div className={styles.fieldValue}>{fmt(selectedInvoice.plan_validity)}</div></div>

              <div><span className={styles.fieldLabel}>Invoice Amount (Base)</span><div className={styles.fieldValue}>₹{selectedInvoice.invoice_amount || "0.00"}</div></div>
              <div><span className={styles.fieldLabel}>Net Amount (After Discount)</span><div className={styles.fieldValue}>₹{selectedInvoice.invoice_amount_net || "0.00"}</div></div>
              <div><span className={styles.fieldLabel}>Discount Code</span><div className={styles.fieldValue}>{fmt(selectedInvoice.discount_code)}</div></div>
              <div><span className={styles.fieldLabel}>Discount Amount</span><div className={styles.fieldValue}>₹{selectedInvoice.discount_amount || "0.00"}</div></div>

              <div><span className={styles.fieldLabel}>HSN Code</span><div className={styles.fieldValue}>{fmt(selectedInvoice.hsn_code)}</div></div>
              <div><span className={styles.fieldLabel}>Total Invoice (Inc. GST)</span><div className={`${styles.fieldValue} ${styles.textBold}`}>₹{selectedInvoice.total_invoice_amount || "0.00"}</div></div>

              <div><span className={styles.fieldLabel}>CGST Rate / Amount</span><div className={styles.fieldValue}>{selectedInvoice.cgst_rate || 0}% / ₹{selectedInvoice.cgst || "0.00"}</div></div>
              <div><span className={styles.fieldLabel}>SGST Rate / Amount</span><div className={styles.fieldValue}>{selectedInvoice.sgst_rate || 0}% / ₹{selectedInvoice.sgst || "0.00"}</div></div>
              <div><span className={styles.fieldLabel}>IGST Rate / Amount</span><div className={styles.fieldValue}>{selectedInvoice.igst_rate || 0}% / ₹{selectedInvoice.igst || "0.00"}</div></div>

              <div>
                <span className={styles.fieldLabel}>Status</span>
                <div className={styles.fieldValue}>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedInvoice.status)}`}>{selectedInvoice.status || "Unknown"}</span>
                </div>
              </div>

              <div><span className={styles.fieldLabel}>Gateway Invoice ID</span><div className={styles.fieldValue}>{fmt(selectedInvoice.invoice_id)}</div></div>
              <div><span className={styles.fieldLabel}>Payment Method</span><div className={styles.fieldValue}>{fmt(selectedInvoice.payment_method)}</div></div>
              <div><span className={styles.fieldLabel}>Service Provider TID</span><div className={styles.fieldValue}>{fmt(selectedInvoice.service_provider_tid)}</div></div>
              <div><span className={styles.fieldLabel}>Created At</span><div className={styles.fieldValue}>{formatDate(selectedInvoice.created_at)}</div></div>
              <div><span className={styles.fieldLabel}>Updated At</span><div className={styles.fieldValue}>{formatDate(selectedInvoice.updated_at)}</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {isCreateModalOpen && (
        <FullscreenModal
          isOpen={isCreateModalOpen}
          title="Create Manual Invoice"
          onClose={() => setIsCreateModalOpen(false)}
          disableBackdropClick={true}
        >
          <CreateInvoiceForm
            onCancel={() => setIsCreateModalOpen(false)}
            onSuccess={() => {
              setIsCreateModalOpen(false);
              fetchInvoices();
            }}
          />
        </FullscreenModal>
      )}

      {/* Edit Draft Invoice Modal */}
      {editingDraftInvoice && (
        <FullscreenModal
          isOpen={!!editingDraftInvoice}
          title="Edit Draft Invoice"
          onClose={() => setEditingDraftInvoice(null)}
          disableBackdropClick={true}
        >
          <CreateInvoiceForm
            initialData={editingDraftInvoice}
            onCancel={() => setEditingDraftInvoice(null)}
            onSuccess={() => {
              setEditingDraftInvoice(null);
              fetchInvoices();
            }}
          />
        </FullscreenModal>
      )}
    </div>
  );
};

export default InvoiceList;
